import type { Frame } from '../../../shared/types'
import { req, type IntegrationsStatus } from '../../lib/api'

/** The `canva` slice of the integrations status, as the server shapes it. */
export interface CanvaIntegrationStatus {
  /** false until the server holds CANVA_CLIENT_ID and CANVA_CLIENT_SECRET */
  enabled: boolean
  connected: boolean
  accountName?: string
  connectedAt?: number
}

export function canvaStatusOf(status: IntegrationsStatus): CanvaIntegrationStatus {
  return (status.canva ?? { enabled: false, connected: false }) as CanvaIntegrationStatus
}

/** A Canva design landed as frames — one per page. Pages already on the
 *  canvas are skipped; pages Canva can't hand over as markup arrive
 *  rasterized. */
export interface CanvaImportResult {
  created: Frame[]
  skipped: number
  pages: number
  editable: boolean
  rasterized: number
  title: string
}

/** A frame pushed to Canva as a new design. `baked` counts the regions
 *  flattened to images because Canva has no element for them. */
export interface CanvaExportResult {
  designId: string
  editUrl?: string
  title: string
  textBoxes: number
  shapes: number
  images: number
  baked: number
  flatBackground: boolean
  verified: boolean
  discardedDesignId?: string
}

/** A pasted canva.com/design/<id>… link — imported through the Canva
 *  integration rather than the page snapshotter. */
export function isCanvaDesignUrl(url: string): boolean {
  return /^https:\/\/(www\.)?canva\.com\/design\/[^/?#]+/i.test(url.trim())
}

export const canvaApi = {
  disconnect: () => req<IntegrationsStatus>('/api/integrations/canva', { method: 'DELETE' }),
  /* both take a while: Canva renders every page (30–90s) or verifies the
     design pixel by pixel (30–120s) before the response lands */
  importDesign: (canvasId: string, url: string) =>
    req<CanvaImportResult>(`/api/canvases/${canvasId}/canva/import`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),
  exportFrame: (canvasId: string, frameId: string, verify?: boolean) =>
    req<CanvaExportResult>(`/api/canvases/${canvasId}/canva/export-frame`, {
      method: 'POST',
      body: JSON.stringify({ frame_id: frameId, ...(verify === undefined ? {} : { verify }) }),
    }),
}
